import { useEffect, useState } from 'react';
import { getFoodReactions } from './api';
import SectionHeader from './SectionHeader';
import StoolScaleBlock from './StoolScaleBlock';
import FastingVomitBlock from './FastingVomitBlock';
import InflammationBlock from './InflammationBlock';
import SymptomScaleBlock from './SymptomScaleBlock';

// Блок «Хронология питания» выключен. Код целиком оставлен на месте —
// чтобы вернуть его, достаточно поставить здесь true. Пока флаг false,
// запрос /food-reactions тоже не выполняется.
const SHOW_FOOD_TIMELINE = false;

const PERIODS = [
  { label: '3 мес', value: 3 },
  { label: '6 мес', value: 6 },
  { label: '12 мес', value: 12 },
];

function parseDMY(s) {
  const [d, m, y] = s.split('.');
  return new Date(2000 + +y, +m - 1, +d);
}

function formatDateLabel(s) {
  const dt = parseDMY(s);
  return dt.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

function getWeekStart(dateStr) {
  const dt = parseDMY(dateStr);
  const d = new Date(dt);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay() + 1);
  return d;
}

function groupByRationPeriodWithWeeks(days) {
  const periods = [];
  let currentPeriod = null;

  days.forEach(day => {
    const rationKey = day.stool_stability
      ? `${day.stool_stability.date_from}-${day.stool_stability.date_to}`
      : 'unknown';

    if (!currentPeriod || currentPeriod.rationKey !== rationKey) {
      if (currentPeriod) {
        periods.push(currentPeriod);
      }
      currentPeriod = {
        rationKey,
        daysData: [day],
        stability: day.stool_stability || null,
      };
    } else {
      currentPeriod.daysData.push(day);
    }
  });

  if (currentPeriod) {
    periods.push(currentPeriod);
  }

  return periods.flatMap(period => {
    const weekBlocks = [];
    let currentWeek = [];

    period.daysData.forEach((day, idx) => {
      currentWeek.push(day);

      if (currentWeek.length === 7 || idx === period.daysData.length - 1) {
        const symptomCounts = {};
        const daysWithSymptoms = new Set();

        currentWeek.forEach(d => {
          if (d.symptoms.length > 0) {
            daysWithSymptoms.add(d.date);
            d.symptoms.forEach(s => {
              const key = `${s.label}|${s.icon}`;
              symptomCounts[key] = (symptomCounts[key] || 0) + 1;
            });
          }
        });

        const weekStartDate = currentWeek[0].date;
        const weekEndDate = currentWeek[currentWeek.length - 1].date;
        const startDt = parseDMY(weekStartDate);
        const endDt = parseDMY(weekEndDate);
        const [sortedStart, sortedEnd] = startDt <= endDt
          ? [weekStartDate, weekEndDate]
          : [weekEndDate, weekStartDate];

        weekBlocks.push({
          startDate: sortedStart,
          endDate: sortedEnd,
          daysData: currentWeek,
          totalDays: currentWeek.length,
          episodeCount: daysWithSymptoms.size,
          symptoms: Object.entries(symptomCounts)
            .map(([key, count]) => {
              const [label, icon] = key.split('|');
              return { label, icon, count };
            })
            .sort((a, b) => b.count - a.count),
          isLastInPeriod: idx === period.daysData.length - 1,
          stability: period.stability,
        });

        currentWeek = [];
      }
    });

    return weekBlocks;
  });
}

/** Бейдж рациона: чувствительность кишечника за период этого рациона.
 *  Процент дней без проблем сюда больше не входит — он помесячный
 *  и живёт в блоке «Чувствительность кала» под тепловой картой. */
function RationSensitivityBadge({ s }) {
  const period = s.is_current
    ? `с ${s.date_from}`
    : `${s.date_from} — ${s.date_to}`;

  const sensScore = s.sensitivity_score;
  const sensText = sensScore != null ? `${sensScore} / 7` : '— / 7';
  const sensClass = sensScore == null ? '' : sensScore < 3.5 ? 'fr-stability-good' : sensScore < 5.5 ? 'fr-stability-mid' : 'fr-stability-bad';
  const scoresRange = s.sensitivity_min != null && s.sensitivity_max != null
    ? `Отмеченные значения: ${s.sensitivity_min}–${s.sensitivity_max}`
    : null;
  const constipText = s.constipation_days > 0 ? `Дней с запором: ${s.constipation_days}` : null;

  return (
    <div className="fr-stability-badge">
      <div className="fr-stability-header">
        <span className="fr-stability-label">Чувствительность кишечника</span>
        <span className={`fr-stability-pct ${sensClass}`}>{sensText}</span>
      </div>
      <div className="fr-stability-sub">
        {s.ration} · {period}
      </div>
      {(scoresRange || constipText) && (
        <div className="fr-stability-sub">
          {'на основе записей с нестабильным стулом'}
          {` (${[scoresRange, constipText].filter(Boolean).join('; ')})`}
        </div>
      )}
    </div>
  );
}

export default function FoodReactionBlock({ petId, docs, onDocsClick }) {
  const [months, setMonths] = useState(3);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!SHOW_FOOD_TIMELINE) return;
    setLoading(true);
    getFoodReactions(petId, months)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [petId, months]);

  const isEmpty = !data || data.days.length === 0;

  return (
    <section className="card-section" id="section-food-reactions">
      {/* Заголовок — тот же SectionHeader, что и у остальных блоков */}
      <SectionHeader
        title="Реакции"
        pdfPath={`/api/pets/${petId}/reactions/pdf`}
        pdfName={`реакции.pdf`}
        periodMode="years_back"
        sectionKey="reactions"
        docs={docs}
        onDocsClick={onDocsClick}
      />

      {/* Тепловая карта консистенции стула (свой период) */}
      <StoolScaleBlock petId={petId} />

      {/* Серии дней без рвоты на голодный желудок (свой период) */}
      <FastingVomitBlock petId={petId} />

      {/* Индекс хронического воспаления (свой выбор года и недель) */}
      <InflammationBlock petId={petId} />

      {/* Прочие симптомы из «Самочувствие/Вес → Симптомы» (свой период) */}
      <SymptomScaleBlock petId={petId} />

      {/* Хронология реакций на питание — выключена флагом SHOW_FOOD_TIMELINE */}
      {SHOW_FOOD_TIMELINE && (
        <>
        <div className="fr-header fr-header-sub">
          <div className="fr-title">Хронология питания</div>
          <div className="visits-period-tabs">
            {PERIODS.map(p => (
              <button
                key={p.value}
                className={`visits-tab${months === p.value ? ' visits-tab-active' : ''}`}
                onClick={() => setMonths(p.value)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loading && <p className="fr-empty">Загрузка…</p>}

        {!loading && isEmpty && (
          <p className="fr-empty">Нет данных за выбранный период</p>
        )}

        {!loading && !isEmpty && (
          <>
            {/* Легенда */}
            <div className="fr-legend-row">
              <span className="fr-legend-chip fr-chip-food">новый продукт</span>
              {data.days.some(d => d.foods.some(f => !f.is_new)) && (
                <span className="fr-legend-chip fr-chip-delta">↕ кол-во</span>
              )}
              <span className="fr-legend-chip fr-chip-treat">лакомство</span>
              {data.legend.length > 0 && (
                <span className="fr-legend-chip fr-chip-symptom">симптом</span>
              )}
            </div>

            {/* Временная шкала */}
            <div className="fr-timeline">
              {groupByRationPeriodWithWeeks(data.days).map((week, wIdx) => (
                <div key={wIdx}>
                  {/* Дни в этой неделе */}
                  {week.daysData.map((day, dIdx) => (
                    <div key={dIdx} className="fr-day">
                      {(day.foods.length > 0 || day.treats.length > 0) && (
                        <>
                          <div className="fr-day-node">
                            <div className="fr-dot" />
                            <span className="fr-date">{formatDateLabel(day.date)}</span>
                          </div>

                          <div className="fr-events">
                            {/* Новые продукты */}
                            {day.foods.filter(f => f.is_new).map((f, j) => (
                              <span key={j} className="fr-chip fr-chip-food">{f.name}</span>
                            ))}
                            {/* Изменения количества */}
                            {day.foods.filter(f => !f.is_new).map((f, j) => (
                              <span key={j} className="fr-chip fr-chip-delta">↕ {f.name}</span>
                            ))}

                            {/* Лакомства и натуральные продукты */}
                            {day.treats.map((t, j) => (
                              <span key={j} className="fr-chip fr-chip-treat">{t}</span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                  {/* Блок с эпизодами симптомов для этой недели */}
                  {week.episodeCount > 0 && (
                    <div className="fr-symptoms-badge">
                      <div className="fr-symptoms-badge-header">
                        <span className="fr-symptoms-dates">
                          {formatDateLabel(week.startDate)} – {formatDateLabel(week.endDate)}
                        </span>
                        <span className="fr-symptoms-count">
                          <strong>{week.episodeCount}</strong> эп. / {week.totalDays} дн.
                        </span>
                      </div>
                      <div className="fr-symptoms-list">
                        {week.symptoms.map((sym, i) => (
                          <div key={i} className="fr-symptom-item">
                            <span className="fr-symptom-icon">{sym.icon}</span>
                            <span className="fr-symptom-text">
                              {sym.count} эп. ({sym.label})
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Бейдж стабильности только в конце каждого периода рациона */}
                  {week.isLastInPeriod && week.stability && (
                    <RationSensitivityBadge s={week.stability} />
                  )}
                </div>
              ))}
            </div>
          </>
        )}
        </>
      )}
    </section>
  );
}
